/**
 * The dashboard's own sign-in interface.
 *
 * Provider-neutral by design. Components depend on this, not on any identity provider's
 * client, so swapping the provider is one file rather than twenty.
 *
 * Two implementations:
 *
 * - **Microsoft Entra** — the real one. Used whenever it is configured.
 * - **Development stub** (`VITE_LOCAL_DEV_AUTH=true`) — bypasses Entra entirely, for
 *   working offline or without a tenant. It takes precedence when set.
 *
 * Both end up in the same place: a token goes to the API, the API answers `/auth/me`
 * with the role it holds. **The role never comes from the token.** The provider knows
 * who someone is; only BDC AI knows what they may do. That is why both paths share
 * `useSessionFromApi` below — so a stubbed sign-in cannot drift from a real one.
 */
import { AUTH_CLAIMS } from '@/config/auth.config'
import { AccountInfo, InteractionRequiredAuthError, LogLevel, PublicClientApplication } from '@azure/msal-browser'
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const LOCAL_DEV_AUTH = import.meta.env.VITE_LOCAL_DEV_AUTH === 'true'

/** The bearer the API accepts when its own sign-in stub is enabled. Not a credential. */
const LOCAL_DEV_BEARER = 'local-dev'

const CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID
const API_URI = import.meta.env.VITE_AUTH_API_URI

/**
 * The delegated scope exposed on the API app registration.
 *
 * A name we chose and asked IT to create, not a fact about any environment — it is the
 * same in every tenant and every deploy. Configuring it would only invite it to drift
 * from the registration it has to match exactly.
 */
const API_SCOPE = `${API_URI}/access_as_user`

const ENTRA_CONFIGURED = Boolean(CLIENT_ID && API_URI)

/**
 * Shaped to carry the claim names the existing principal helpers already read, so the
 * role can live in the API rather than in token claims without touching every screen.
 */
export interface AuthUser {
  sub?: string
  email?: string
  name?: string
  nickname?: string
  email_verified?: boolean
  updated_at?: string
  /** Role and scope claims, read through the principal helpers. */
  [claim: string]: unknown
}

export interface AuthSession {
  user: AuthUser | undefined
  isAuthenticated: boolean
  isLoading: boolean
  /** Non-null when sign-in itself failed, as opposed to nobody being signed in yet. */
  error: Error | undefined
  signIn: () => Promise<void>
  signOut: () => void
  getAccessToken: () => Promise<string>
}

const AuthContext = createContext<AuthSession | undefined>(undefined)

export function useAuth(): AuthSession {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

/**
 * Why a session could not be established.
 *
 * "unreachable" is not a sign-in problem at all — the API is down or still starting, which
 * happens constantly in local development every time it recompiles. Calling that a failed
 * sign-in sends people to re-authenticate against an identity provider that was never the
 * problem.
 */
export type AuthFailure = 'unreachable' | 'no-role' | 'server' | 'sign-in'

export interface AuthError extends Error {
  failure: AuthFailure
}

function authError(failure: AuthFailure, message: string): AuthError {
  return Object.assign(new Error(message), { failure })
}

/** fetch rejects with a TypeError when it never reached the server. */
function isUnreachable(error: unknown): boolean {
  return error instanceof TypeError
}

/** How many times to wait out an API that is still coming up, and how long to start at. */
const UNREACHABLE_RETRIES = 4
const RETRY_BASE_MS = 1000

interface CurrentUserResponse {
  subject: string
  email?: string
  name?: string
  role: string
}

/**
 * Turns a token into a session by asking the API who the holder is.
 *
 * Shared by both implementations on purpose: whatever proved the identity, what the
 * person may do is decided the same way.
 */
function useSessionFromApi(
  getAccessToken: () => Promise<string>,
  enabled: boolean,
): { user: AuthUser | undefined; isLoading: boolean; error: Error | undefined } {
  /**
   * What was loaded, and which `enabled` it describes.
   *
   * Kept together on purpose. Holding "loading" in its own state left a window of one
   * render where `enabled` had already flipped to true but a previous run's "finished"
   * was still on display — effects run after render, so nothing had corrected it yet.
   * A guard reading that render saw "done loading, nobody signed in" and bounced to the
   * identity provider, discarding a session that had just succeeded.
   */
  const [result, setResult] = useState<{ describes: boolean; user?: AuthUser; error?: Error } | null>(null)

  useEffect(() => {
    if (!enabled) {
      setResult({ describes: false })
      return
    }

    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined

    const load = async (attempt = 0) => {
      try {
        const token = await getAccessToken()
        const base = import.meta.env.VITE_API_BASE_URL || ''
        const response = await fetch(`${base}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })

        if (!response.ok) {
          // 401 means the account signed in but holds no role — a refusal, not a bug.
          throw response.status === 401
            ? authError('no-role', 'Signed in, but no dashboard role has been granted for this account.')
            : authError('server', `Could not load the current user (${response.status})`)
        }

        const me: CurrentUserResponse = await response.json()
        if (cancelled) return

        setResult({
          describes: true,
          user: {
            sub: me.subject,
            email: me.email,
            name: me.name,
            [AUTH_CLAIMS.ROLE]: me.role,
          },
        })
      } catch (err) {
        if (cancelled) return

        // The API restarts on every recompile locally, so a request that lands mid-restart is
        // expected rather than exceptional. Wait it out a few times before giving up.
        if (isUnreachable(err) && attempt < UNREACHABLE_RETRIES) {
          retryTimer = setTimeout(() => void load(attempt + 1), RETRY_BASE_MS * 2 ** attempt)
          return
        }

        const error = isUnreachable(err)
          ? authError('unreachable', 'Cannot reach the API. It may still be starting up — check that it is running.')
          : err instanceof Error
            ? (err as AuthError)
            : authError('sign-in', String(err))

        setResult({ describes: true, error })
      }
    }

    void load()
    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [getAccessToken, enabled])

  return {
    user: result?.user,
    // Derived, so it is correct in the same render that `enabled` changes.
    isLoading: result?.describes !== enabled,
    error: result?.error,
  }
}

// --- Diagnostics -------------------------------------------------------------------

/**
 * A sign-in loop is full page navigations, so the console is wiped on every pass and
 * there is nothing left to read afterwards. These notes survive in sessionStorage.
 *
 * Values are never recorded — only whether a thing was present — so an authorization
 * code or token cannot end up in storage. Read with:
 *   JSON.parse(sessionStorage['bdc-auth-trace'])
 */
const TRACE_KEY = 'bdc-auth-trace'

function trace(event: string, detail?: Record<string, unknown>) {
  try {
    const entries: unknown[] = JSON.parse(sessionStorage.getItem(TRACE_KEY) ?? '[]')
    entries.push({ at: new Date().toISOString(), event, ...detail })
    sessionStorage.setItem(TRACE_KEY, JSON.stringify(entries.slice(-40)))
  } catch {
    // Tracing must never be the reason sign-in fails.
  }
  console.info(`[auth] ${event}`, detail ?? '')
}

/**
 * Caps how many times sign-in may be started without a session resulting.
 *
 * A guard held in component state cannot stop this: every attempt is a fresh page load,
 * which resets it. Only something outliving the navigation can.
 */
const ATTEMPT_KEY = 'bdc-auth-attempts'
const MAX_ATTEMPTS = 3

function countAttempt(): number {
  try {
    const next = Number(sessionStorage.getItem(ATTEMPT_KEY) ?? '0') + 1
    sessionStorage.setItem(ATTEMPT_KEY, String(next))
    return next
  } catch {
    return 1
  }
}

function clearAttempts() {
  try {
    sessionStorage.removeItem(ATTEMPT_KEY)
  } catch {
    // Nothing to do — the cap is a safety net, not a correctness requirement.
  }
}

// --- Microsoft Entra ---------------------------------------------------------------

const msal = ENTRA_CONFIGURED
  ? new PublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        // Multi-tenant: any directory that has consented. `/common` would also admit
        // personal Microsoft accounts.
        authority: 'https://login.microsoftonline.com/organizations',
        redirectUri: window.location.origin,
        postLogoutRedirectUri: `${window.location.origin}/login`,
      },
      cache: {
        // Survives a reload without persisting a session beyond the browser session.
        cacheLocation: 'sessionStorage',
      },
      system: {
        loggerOptions: {
          // Off by default, so a redirect failure says nothing. On in development only.
          logLevel: import.meta.env.DEV ? LogLevel.Verbose : LogLevel.Error,
          piiLoggingEnabled: false,
          loggerCallback: (_level, message, containsPii) => {
            if (containsPii) return
            console.info('[msal]', message)
          },
        },
      },
    })
  : null

/**
 * Initialise MSAL and consume the redirect response exactly once, outside React.
 *
 * `handleRedirectPromise` may only be called once per redirect — the first call consumes
 * the response. StrictMode double-invokes effects in development, so doing this inside
 * one consumes the response on the first pass and finds nothing on the second, which
 * reads as a failed sign-in and sends the user round again. A single module-level
 * promise makes the number of mounts irrelevant.
 */
const msalReady: Promise<AccountInfo | null> = msal
  ? (() => {
      const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''))
      trace('page-load', {
        path: window.location.pathname,
        // Presence only. The code itself is a credential and is never recorded.
        hasCode: fragment.has('code'),
        hasError: fragment.has('error'),
        error: fragment.get('error') ?? undefined,
        errorDescription: fragment.get('error_description') ?? undefined,
      })

      return msal
        .initialize()
        .then(() => msal!.handleRedirectPromise())
        .then(result => {
          if (result?.account) {
            msal!.setActiveAccount(result.account)
          }
          const existing = msal!.getActiveAccount() ?? msal!.getAllAccounts()[0] ?? null
          if (existing) {
            msal!.setActiveAccount(existing)
          }
          trace('redirect-handled', {
            responseHadAccount: !!result?.account,
            cachedAccounts: msal!.getAllAccounts().length,
            resolvedAccount: !!existing,
          })
          return existing
        })
        .catch(err => {
          trace('redirect-failed', {
            name: err?.name,
            errorCode: err?.errorCode,
            message: err?.message,
          })
          throw err
        })
    })()
  : Promise.resolve(null)

function useEntraSession(): AuthSession {
  const [account, setAccount] = useState<AccountInfo | null>(null)
  const [ready, setReady] = useState(false)
  const [signInError, setSignInError] = useState<Error | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    msalReady
      .then(existing => {
        if (!cancelled) {
          setAccount(existing)
          setReady(true)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setSignInError(err instanceof Error ? err : new Error(String(err)))
          setReady(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const getAccessToken = useCallback(async () => {
    const active = msal!.getActiveAccount() ?? msal!.getAllAccounts()[0]
    if (!active) {
      throw new Error('Not signed in')
    }
    try {
      const result = await msal!.acquireTokenSilent({ scopes: [API_SCOPE], account: active })
      return result.accessToken
    } catch (err) {
      // The refresh token expired or consent is needed — only a redirect can fix it.
      if (err instanceof InteractionRequiredAuthError) {
        await msal!.acquireTokenRedirect({ scopes: [API_SCOPE], account: active })
      }
      throw err
    }
  }, [])

  const { user, isLoading, error } = useSessionFromApi(getAccessToken, ready && !!account)

  // A session was established, so the run of attempts is over.
  useEffect(() => {
    if (user) {
      clearAttempts()
    }
  }, [user])

  const signIn = useCallback(async () => {
    const attempt = countAttempt()
    if (attempt > MAX_ATTEMPTS) {
      const message =
        `Sign-in was started ${attempt - 1} times without a session being established. ` +
        `Stopping rather than redirecting again — see JSON.parse(sessionStorage['bdc-auth-trace']).`
      trace('give-up', { attempt })
      setSignInError(new Error(message))
      return
    }
    trace('sign-in-start', { attempt })
    await msal!.loginRedirect({ scopes: [API_SCOPE] })
  }, [])

  const signOut = useCallback(() => {
    void msal!.logoutRedirect()
  }, [])

  return useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      // Still loading while MSAL settles, so guards do not bounce to sign-in early.
      isLoading: !ready || (!!account && isLoading),
      error: signInError ?? error,
      signIn,
      signOut,
      getAccessToken,
    }),
    [user, ready, account, isLoading, signInError, error, signIn, signOut, getAccessToken],
  )
}

// --- Local development stub --------------------------------------------------------

/**
 * Bypasses Entra entirely. Authentication only — the API still resolves the role from
 * `dashboard_users` and refuses when there is none, so local behaviour matches deployed
 * behaviour.
 */
function useDevSession(): AuthSession {
  const getAccessToken = useCallback(async () => LOCAL_DEV_BEARER, [])
  const { user, isLoading, error } = useSessionFromApi(getAccessToken, true)

  return useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      error,
      signIn: async () => {
        // Already signed in while stubbed — nothing to redirect to.
      },
      signOut: () => {
        console.info('[local-dev-auth] sign-out is a no-op while sign-in is stubbed')
      },
      getAccessToken,
    }),
    [user, isLoading, error, getAccessToken],
  )
}

// --- Neither configured ------------------------------------------------------------

/** Reports the gap rather than failing obscurely, so nobody stares at a blank screen. */
function useUnconfiguredSession(): AuthSession {
  const error = useMemo(
    () =>
      new Error(
        'Dashboard sign-in is not configured. Set VITE_AUTH_CLIENT_ID and VITE_AUTH_API_URI, ' +
          'or VITE_LOCAL_DEV_AUTH=true to bypass sign-in locally.',
      ),
    [],
  )

  return useMemo(
    () => ({
      user: undefined,
      isAuthenticated: false,
      isLoading: false,
      error,
      signIn: async () => {
        throw error
      },
      signOut: () => {},
      getAccessToken: async () => {
        throw error
      },
    }),
    [error],
  )
}

function EntraAuthProvider({ children }: { children: ReactNode }) {
  return <AuthContext.Provider value={useEntraSession()}>{children}</AuthContext.Provider>
}

function DevAuthProvider({ children }: { children: ReactNode }) {
  return <AuthContext.Provider value={useDevSession()}>{children}</AuthContext.Provider>
}

function UnconfiguredAuthProvider({ children }: { children: ReactNode }) {
  return <AuthContext.Provider value={useUnconfiguredSession()}>{children}</AuthContext.Provider>
}

/**
 * Picks the implementation by rendering one or the other. Choosing a component rather
 * than calling one hook or the other keeps the hook order stable.
 *
 * The stub wins when set, so a developer can bypass Entra even on a machine that has
 * the real configuration.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  if (LOCAL_DEV_AUTH) {
    return <DevAuthProvider>{children}</DevAuthProvider>
  }
  if (ENTRA_CONFIGURED) {
    return <EntraAuthProvider>{children}</EntraAuthProvider>
  }
  return <UnconfiguredAuthProvider>{children}</UnconfiguredAuthProvider>
}
